import type {EventVOMinifiedWizardPrefill} from '@Components/events/events.types';
import {EVENT_ERROR_LOG_TAG} from '@Components/event-wizard/event-wizard.types';
import {onEventWizardCoverPhotoUpdate, onEventWizardStyleFontUpdate} from '@Components/event-wizard/event-wizard-slice';
import {MediaTypes} from '@Components/event-wizard/event-wizard-image-video/event-wizard-image-video.types';
import {applyEventWizardColorLegibility} from '@Components/event-wizard/event-wizard.helper';
import {getEventWizardContentColor} from '@Components/event-wizard/event-wizard.hooks';
import {updateStyleFont} from '@Components/event-wizard/preview/preview.helper';
import type {ColorLegibilityState} from '@Libraries/event-colors.library';
import {applyBackgroundSelection, dismissLegibilityBanner} from '@Libraries/event-colors.library';
import {addFontsAsync} from '@Libraries/font-library';
import {logClientError} from '@Libraries/log.library';

/**
 * Applies the event preset the server picked for the gallery category the user came from: its image as the cover
 * (remembered by the image's hashed filename, which is what the save needs), its background through the same
 * legibility path as a picked background, and its font once it has loaded.
 */
export const setReduxForEventPresetPrefill = (eventVOMinifiedWizardPrefill?: EventVOMinifiedWizardPrefill): void => {
  const eventPresetImageHash = eventVOMinifiedWizardPrefill?.eventPresetImageHash ?? null;
  if (eventPresetImageHash === null || !eventVOMinifiedWizardPrefill?.coverPreviewURL) {
    return;
  }

  window.PMW.redux.store.dispatch(
    onEventWizardCoverPhotoUpdate({
      uploadedUrl: eventVOMinifiedWizardPrefill.coverPreviewURL,
      mediaType: MediaTypes.UPLOAD,
      eventPresetImageHash,
    })
  );

  if (eventVOMinifiedWizardPrefill.backgroundColor !== null) {
    applyEventPresetBackground(eventVOMinifiedWizardPrefill.backgroundColor);
  }

  if (eventVOMinifiedWizardPrefill.fontFamily !== null) {
    void applyEventPresetFont(eventVOMinifiedWizardPrefill.fontFamily);
  }
};

/**
 * The "text colour adjusted" banner is dismissed straight away: the user has not chosen anything yet, so there is
 * nothing of theirs for Undo to bring back.
 */
const applyEventPresetBackground = (backgroundColor: string): void => {
  const legibilityStateWithPresetBackground = applyBackgroundSelection(getCurrentColorLegibilityState(), backgroundColor);
  applyEventWizardColorLegibility(dismissLegibilityBanner(legibilityStateWithPresetBackground));
};

const getCurrentColorLegibilityState = (): ColorLegibilityState => {
  const state = window.PMW.redux.store.getState();

  return {
    background: state.eventWizard.styles.colors.background ?? null,
    contentColor: getEventWizardContentColor(state),
    colorToRestoreOnUndo: state.eventWizard.styleLegibility.colorToRestoreOnUndo,
    status: state.eventWizard.styleLegibility.status,
  };
};

/**
 * A font that fails to load leaves the default font in place rather than rendering the preset's font as a fallback face.
 */
const applyEventPresetFont = async (fontFamily: string): Promise<void> => {
  try {
    await addFontsAsync([fontFamily]);
  } catch (error) {
    await logClientError(error, {reason: 'Gallery preset font failed to load', fontFamily, tag: EVENT_ERROR_LOG_TAG});
    return;
  }

  window.PMW.redux.store.dispatch(onEventWizardStyleFontUpdate(fontFamily));
  updateStyleFont(fontFamily);
};
